import { AppColorDefinition } from '@agilox/common';
import { LinkTarget, Position, Positions, Size, UITheme, UIThemes } from '@agilox/ui-common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	output,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Params, RouterLink } from '@angular/router';
import { distinctUntilChanged, map } from 'rxjs';
import { IconModule } from '../icon/icon.module';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TooltipModule } from '../tooltip/tooltip.module';
import { BUTTON_SPINNER_CONFIG } from './button-spinner.config';
import { BUTTON_STYLE_CONFIG } from './button-styles.config';

@Component({
	selector: 'ui-button',
	imports: [IconModule, SpinnerComponent, NgTemplateOutlet, RouterLink, TooltipModule],
	templateUrl: './button.component.html',
	encapsulation: ViewEncapsulation.None,
})
export class ButtonComponent {
	private breakpointObserver = inject(BreakpointObserver);

	type = input<UITheme | 'link' | 'link-dark' | 'link-error' | 'blue-secondary'>(UIThemes.Primary);
	text = input<string>('');
	iconSize = input<Exclude<Size, '2xl' | '3xl'>>('l');
	fullWidth = input<boolean>(false);
	rightAligned = input<boolean>(false);
	inverted = input<boolean>(false);
	disabled = input<boolean>(false);
	showSpinner = input<boolean>(false);
	loadingText = input<string | undefined>(undefined);
	icon = input<string>('');
	iconPosition = input<Extract<Position, 'left' | 'right'>>(Positions.Left);
	color = input<AppColorDefinition | undefined>();
	hideTextOnMobile = input<boolean>(false);
	fullWidthMobile = input<boolean>(false);
	tabindex = input<number>(0);
	styleClass = input<string>('');
	tooltip = input<string>('');
	tooltipPosition = input<Position>(Positions.Top);
	ariaLabel = input<string | undefined>(undefined);
	href = input<string | undefined>(undefined);
	route = input<string | string[] | undefined>(undefined);
	queryParams = input<Params | undefined>(undefined);
	target = input<LinkTarget | undefined>(undefined);

	buttonClicked = output<Event>();

	focusable = viewChild<ElementRef<HTMLElement>>('focusable');

	renderAs = computed<'button' | 'anchor-href' | 'anchor-router'>(() => {
		if (this.route() != null) {
			return 'anchor-router';
		}
		if (this.href() != null) {
			return 'anchor-href';
		}
		return 'button';
	});

	rel = computed<string | null>(() =>
		this.href() != null && this.target() === '_blank' ? 'noopener noreferrer' : null
	);

	noTextOnMobile = computed(() => this.hideTextOnMobile() && this._isMobile());

	displayLoadingText = computed(() => this.showSpinner() && this.loadingText() !== undefined);

	showIcon = computed(() => !!this.icon() && !this.displayLoadingText());

	iconOnLeft = computed(() => this.showIcon() && this.iconPosition() === Positions.Left);

	iconOnRight = computed(() => this.showIcon() && this.iconPosition() === Positions.Right);

	spinnerConfig = computed(() => {
		const config = BUTTON_SPINNER_CONFIG[this.type()];
		if (!config) {
			return { inverted: false, color: 'primary' as const };
		}
		return this.hovered() ? config.hover : config.default;
	});

	styles = computed(() => {
		const type = this.type();
		let returnString = this.getBaseStyles();
		const config = BUTTON_STYLE_CONFIG[type];
		if (config) {
			returnString += config.base ?? '';
			if (this.disabled()) {
				returnString += config.disabled;
			} else if (this.inverted() && config.inverted) {
				returnString += config.inverted;
			} else {
				returnString += config.default;
			}
		}

		returnString += this.computeEdgeCaseStyles();
		returnString +=
			'  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

		if (this.styleClass()) {
			returnString += ` ${this.styleClass()}`;
		}

		return returnString;
	});

	colorStyles = computed(() => {
		const color = this.color();
		const hovered = this.hovered();

		if (color) {
			return {
				backgroundColor: hovered ? color.buttonHover : color.color,
				borderColor: hovered ? color.buttonHover : color.color,
			};
		}
		return {
			backgroundColor: '',
			borderColor: '',
		};
	});

	hovered = signal<boolean>(false);

	private _isMobile = toSignal(
		this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
			map((result) => result.matches),
			distinctUntilChanged()
		),
		{ initialValue: false }
	);

	clicked(event: MouseEvent) {
		if (!this.disabled()) {
			this.buttonClicked.emit(event);
		}
	}

	onHover(hover: boolean) {
		this.hovered.set(hover);
	}

	public focus(): void {
		this.focusable()?.nativeElement.focus();
	}

	private getBaseStyles(): string {
		return (
			'items-center rounded h-10 text-base' +
			' flex justify-center overflow-hidden' +
			' px-4 py-3 relative text-center decoration-0 text-ellipsis' +
			' whitespace-nowrap'
		);
	}

	private computeEdgeCaseStyles(): string {
		let returnString = '';
		// FULL WIDTH
		if (this.fullWidth() || this.fullWidthMobile()) {
			returnString += ' w-full';
		}
		// ICON ONLY
		if (this.icon() && !this.text()) {
			returnString += ' p-2 w-10';
		}
		// WITH ICON
		if (this.icon() && this.text()) {
			returnString += ' items-center flex justify-center w-fit transition-300 transition-[fill]';
		}
		if (
			this.renderAs() !== 'button' &&
			!this.fullWidth() &&
			!this.fullWidthMobile() &&
			!this.icon()
		) {
			returnString += ' w-fit';
		}
		if (this.disabled() && this.renderAs() !== 'button') {
			returnString += ' pointer-events-none';
		}
		return returnString;
	}
}
